import { SESSION_ENDPOINT, type SessionResponse, type SessionStatus, TURNSTILE_ACTION } from "../../shared/llm-api";

/**
 * Player session: before the first LLM call the browser proves it's a person
 * with an invisible Turnstile challenge and trades the token for an HttpOnly
 * session cookie (Worker: worker/session.ts). Usually finished while the
 * player is still on the title screen.
 */

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render(
    el: HTMLElement,
    opts: {
      sitekey: string;
      action: string;
      appearance?: "always" | "execute" | "interaction-only";
      callback: (token: string) => void;
      "error-callback": () => void;
    },
  ): string;
  remove(id: string): void;
}

let pending: Promise<void> | null = null;
let scriptLoad: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  scriptLoad ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => {
      const api = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
      if (api) resolve(api);
      else reject(new Error("turnstile missing"));
    };
    s.onerror = () => reject(new Error("turnstile script failed"));
    document.head.appendChild(s);
  }).catch((e: unknown) => {
    scriptLoad = null;
    throw e;
  });
  return scriptLoad;
}

/** Run the challenge; resolves with a single-use token. Only shows UI if Turnstile insists. */
async function challenge(siteKey: string): Promise<string> {
  const api = await loadTurnstile();
  const box = document.createElement("div");
  box.id = "turnstileBox";
  document.body.appendChild(box);
  try {
    return await new Promise<string>((resolve, reject) => {
      const id = api.render(box, {
        sitekey: siteKey,
        action: TURNSTILE_ACTION,
        appearance: "interaction-only",
        callback: (token) => {
          resolve(token);
          api.remove(id);
        },
        "error-callback": () => {
          reject(new Error("turnstile challenge failed"));
          api.remove(id);
        },
      });
    });
  } finally {
    box.remove();
  }
}

async function establish(): Promise<void> {
  const status = (await (await fetch(SESSION_ENDPOINT, { credentials: "same-origin" })).json()) as SessionStatus;
  if (status.active) return;
  if (!status.siteKey) throw new Error("no site key");
  const token = await challenge(status.siteKey);
  const res = await fetch(SESSION_ENDPOINT, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const body = (await res.json()) as SessionResponse;
  if (!body.ok) throw new Error(`session refused: ${body.code}`);
}

/** Make sure a session cookie exists. Concurrent callers share one attempt; a failure can be retried. */
export function ensureSession(): Promise<void> {
  pending ??= establish().catch((e: unknown) => {
    pending = null;
    throw e;
  });
  return pending;
}

/** The server said our session is gone (expired or unknown): forget it so the next call starts a new one. */
export function dropSession(): void {
  pending = null;
}

/** Start the handshake early (title screen) so the first conversation doesn't wait for it. */
export function prewarmSession(): void {
  ensureSession().catch(() => undefined);
}
